import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Analytics from "@/pages/Analytics";
import SiteSettings from "@/pages/SiteSettings";
import Services from "@/pages/Services";
import ServiceShowcases from "@/pages/ServiceShowcases";
import WhyChoose from "@/pages/WhyChoose";
import ProcessSteps from "@/pages/ProcessSteps";
import Challenges from "@/pages/Challenges";
import Industries from "@/pages/Industries";
import Portfolio from "@/pages/Portfolio";
import Pricing from "@/pages/Pricing";
import Stats from "@/pages/Stats";
import ClientLogos from "@/pages/ClientLogos";
import Testimonials from "@/pages/Testimonials";
import FAQ from "@/pages/FAQ";
import Blog from "@/pages/Blog";
import Team from "@/pages/Team";
import Media from "@/pages/Media";
import Navigation from "@/pages/Navigation";
import Banners from "@/pages/Banners";
import Popups from "@/pages/Popups";
import Leads from "@/pages/Leads";
import SEO from "@/pages/SEO";
import Users from "@/pages/Users";
import Roles from "@/pages/Roles";
import ActivityLogs from "@/pages/ActivityLogs";
import Settings from "@/pages/Settings";
import UxAudit from "@/pages/UxAudit";
import HomeBuilder from "@/pages/HomeBuilder";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass rounded-xl p-6 flex items-center gap-3 text-white/60 text-sm">
          <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          Loading workspace…
        </div>
      </div>
    );
  }
  if (!user) {
    const next = `${window.location.pathname}${window.location.search || ""}`;
    const q = next && next !== "/" ? `?next=${encodeURIComponent(next)}` : "";
    return <Navigate to={`/login${q}`} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/site-settings" element={<SiteSettings />} />
        <Route path="/home-builder" element={<HomeBuilder />} />
        <Route path="/services" element={<Services />} />
        <Route path="/service-showcases" element={<ServiceShowcases />} />
        <Route path="/why-choose" element={<WhyChoose />} />
        <Route path="/process" element={<ProcessSteps />} />
        <Route path="/challenges" element={<Challenges />} />
        <Route path="/industries" element={<Industries />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/stats" element={<Stats />} />
        <Route path="/logos" element={<ClientLogos />} />
        <Route path="/testimonials" element={<Testimonials />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/team" element={<Team />} />
        <Route path="/media" element={<Media />} />
        <Route path="/navigation" element={<Navigation />} />
        <Route path="/banners" element={<Banners />} />
        <Route path="/popups" element={<Popups />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/seo/*" element={<SEO />} />
        <Route path="/seo" element={<SEO />} />
        <Route path="/users" element={<Users />} />
        <Route path="/roles" element={<Roles />} />
        <Route path="/activity" element={<ActivityLogs />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/ux-audit" element={<UxAudit />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
